import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_TOKEN_REFRESH } from './api.endpoint';

const baseURL = process.env.REACT_APP_IP_ADDRESS

export interface RefreshResponse {
  access: string;
  refresh?: string;
}

const http = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
  // Если фронт и бэк на разных доменах + используете cookie-вариант — раскомментируйте:
  withCredentials: true,
});

/* ---------- REQUEST INTERCEPTOR: подшиваем access-token ---------- */
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const access = localStorage.getItem('accessToken');
    if (access) {
      config.headers.Authorization = `Bearer ${access}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/* ---------- RESPONSE INTERCEPTOR: тихое обновление access-token ---------- */
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as AxiosRequestConfig & { _retry?: boolean };

    /* 401 возникающая не из-за опечатки в пароле, а из-за просроченного access-токена */
    if (error.response?.status === 401 && !original?._retry) {
      original._retry = true;

      /* Чтобы при множестве параллельных запросов refresh не ушёл десятки раз */
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (original.headers) original.headers.Authorization = `Bearer ${token}`;
            return http(original);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;
      const refresh = localStorage.getItem('refreshToken');

      try {
        const { data } = await axios.post<RefreshResponse>(
          API_TOKEN_REFRESH,
          { refresh },
          { headers: { 'Content-Type': 'application/json' } },
        );

        localStorage.setItem('accessToken', data.access);
        if (data.refresh) localStorage.setItem('refreshToken', data.refresh);

        processQueue(null, data.access);

        /* повторяем исходный запрос */
        if (original.headers) original.headers.Authorization = `Bearer ${data.access}`;
        return http(original);
      } catch (refreshErr) {
        processQueue(refreshErr as AxiosError, null);
        /* здесь удобно вызвать logout() или редирект на страницу входа */
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default http;
